/**
 * AIChatPanel — Peek panel for AI chat (mock implementation).
 * Real AI wiring deferred to FreshVibe Studio integration.
 */
import React, { useState } from 'react';
import { Box, Group, Text, TextInput, ActionIcon, Badge } from '@mantine/core';
import { IconSparkles } from '@tabler/icons-react';
import { usePeek } from './PeekManager';

export const AIChatPanel: React.FC = () => {
  const { closePeek } = usePeek();
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<Array<{role: string; content: string}>>([
    { role: 'assistant', content: 'Hi! I can help with your documents. Try asking me to summarize the current page, suggest improvements, generate content, or explain concepts.' }
  ]);

  const suggestions = ['Summarize this page', 'Suggest improvements', 'Generate a list', 'Explain this concept'];

  const handleSend = () => {
    if (!message.trim()) return;
    setMessages(prev => [...prev, { role: 'user', content: message }]);
    setMessage('');
  };

  return (
    <Box style={{ position: 'fixed', top: 50, right: 0, width: 380, height: 'calc(100vh - 50px)', backgroundColor: 'var(--fc-bg-primary)', boxShadow: '-2px 0 12px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', zIndex: 100 }}>
      <Box p="md" style={{ borderBottom: '1px solid var(--fc-border-primary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Group gap="sm">
          <IconSparkles size={18} color="var(--fc-accent-primary)" />
          <Text fw={600}>AI Assistant</Text>
          <Badge size="xs" variant="light" color="violet">Beta</Badge>
        </Group>
        <ActionIcon variant="subtle" size="sm" onClick={() => closePeek()}>✕</ActionIcon>
      </Box>
      <Box style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        <Group gap="md" style={{ flexDirection: 'column' }}>
          {messages.map((msg, i) => (
            <Box key={i} p="sm" style={{ background: msg.role === 'assistant' ? 'var(--fc-bg-secondary)' : '#e3e5f5', borderRadius: 8, maxWidth: '85%', alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <Text size="sm">{msg.content}</Text>
            </Box>
          ))}
        </Group>
      </Box>
      <Box px="md" pb="xs">
        <Text size="xs" c="dimmed" mb="xs">Try asking:</Text>
        <Group gap="xs">
          {suggestions.map((s, i) => (
            <Badge key={i} size="sm" variant="light" style={{ cursor: 'pointer' }} onClick={() => setMessage(s)}>{s}</Badge>
          ))}
        </Group>
      </Box>
      <Box p="md" style={{ borderTop: '1px solid var(--fc-border-primary)' }}>
        <Group gap="xs">
          <Box style={{ flex: 1 }}>
            <TextInput placeholder="Ask AI..." size="sm" value={message} onChange={e => setMessage(e.target.value)}
              styles={{ input: { borderRadius: 20, paddingLeft: 16, paddingRight: 16 } }}
              onKeyDown={e => { if (e.key === 'Enter' && message.trim()) handleSend(); }} />
          </Box>
          <ActionIcon color="violet" variant="filled" size="lg" radius="xl" onClick={handleSend}><IconSparkles size={16} /></ActionIcon>
        </Group>
      </Box>
    </Box>
  );
};